import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { adminAuth, adminApi } from "@/lib/api";
import { debug } from "@/lib/log";
import { toast } from "sonner";
import DeepSeekUsage from "@/pages/admin/DeepSeekUsage";
import SearchAnalytics from "@/pages/admin/SearchAnalytics";
import AdminDashboardCards from "@/pages/admin/AdminDashboardCards";
import DrugManagement from "@/pages/admin/DrugManagement";
import AIModelsManagement from "@/pages/admin/AIModelsManagement";
import AccountSecurity from "@/pages/admin/AccountSecurity";
import {
  Lock,
  Plus,
  Trash2,
  Loader2,
  LogOut,
  ShieldCheck,
  PillBottle,
  Pencil,
  Calendar,
  KeyRound,
  X,
  Activity,
  BrainCircuit,
  Search as SearchIcon,
} from "lucide-react";

const STAGE_CHECKING = "checking";
const STAGE_GATE = "gate";
const STAGE_LOGIN = "login";
const STAGE_DASHBOARD = "dashboard";

const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return e?.message || "Something went wrong";
};

// Extract lockout payload from a 429 response, if present.
const getLockDetail = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.message) {
    return {
      message: d.message,
      retry_after: Number(d.retry_after) || 0,
      stage: d.stage,
      level: Number(d.level) || 1,
    };
  }
  return null;
};

// Format YYYY/MM/DD from any digit input (max 8 digits).
const formatBirthdate = (v) => {
  const digits = String(v || "").replace(/\D+/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return digits.slice(0, 4) + "/" + digits.slice(4);
  return digits.slice(0, 4) + "/" + digits.slice(4, 6) + "/" + digits.slice(6);
};

const BIRTHDATE_RE = /^\d{4}\/\d{2}\/\d{2}$/;

// Human-friendly remaining time in Arabic.
const humanRemaining = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  if (s <= 0) return "";
  if (s < 60) return `${s} ثانية`;
  const mins = Math.ceil(s / 60);
  if (mins < 60) return `${mins} دقيقة`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs} ساعة و${rem} دقيقة` : `${hrs} ساعة`;
};

export default function Admin() {
  const [stage, setStage] = useState(STAGE_CHECKING);
  const [drugs, setDrugs] = useState([]);
  const [stats, setStats] = useState({ total: 0, unique_ar: 0, unique_en: 0, documents: 0 });
  const [loading, setLoading] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [openCard, setOpenCard] = useState(null); // "drugs" | "ai" | "account" | null
  const [me, setMe] = useState(null);

  const loadDrugs = useCallback(async () => {
    setLoading(true);
    try {
      const [data, s] = await Promise.all([adminApi.list(), adminApi.stats()]);
      setDrugs(data.results || []);
      setStats(s || { total: 0, unique_ar: 0, unique_en: 0, documents: 0 });
    } catch (e) {
      if (e?.response?.status === 401) {
        setStage(STAGE_GATE);
      } else {
        toast.error(formatErr(e));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount, check existing session
  useEffect(() => {
    let cancelled = false;
    adminAuth
      .me()
      .then((data) => {
        if (!cancelled) {
          setMe(data);
          setStage(STAGE_DASHBOARD);
          loadDrugs();
        }
      })
      .catch(() => !cancelled && setStage(STAGE_GATE));
    return () => {
      cancelled = true;
    };
  }, [loadDrugs]);

  const handleLogout = async () => {
    try {
      await adminAuth.logout();
    } catch (err) {
      debug("logout call failed:", err);
    }
    setDrugs([]);
    setStats({ total: 0, unique_ar: 0, unique_en: 0, documents: 0 });
    setStage(STAGE_GATE);
    toast.success("تم تسجيل الخروج");
  };

  const handleLogoutAll = async () => {
    if (!window.confirm("سيتم تسجيل الخروج من جميع الأجهزة. هل أنت متأكد؟")) return;
    try {
      await adminAuth.logoutAll();
    } catch (err) {
      debug("logout-all call failed:", err);
    }
    setDrugs([]);
    setStats({ total: 0, unique_ar: 0, unique_en: 0, documents: 0 });
    setStage(STAGE_GATE);
    toast.success("تم تسجيل الخروج من جميع الأجهزة");
  };

  const handleDelete = async (drug) => {
    if (!window.confirm(`هل تريد حذف "${drug.name}" من قائمة التقييد؟`)) return;
    try {
      await adminApi.remove(drug.id);
      setDrugs((arr) => arr.filter((d) => d.id !== drug.id));
      // Refresh stats since removing a compound record can drop several names.
      adminApi.stats().then(setStats).catch(() => {});
      toast.success("تم الحذف");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // -------- STAGE: checking --------
  if (stage === STAGE_CHECKING) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  // -------- STAGE: gate (birth date) --------
  if (stage === STAGE_GATE) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-md mx-auto mt-16 sm:mt-24 px-6">
          <GateForm onPass={() => setStage(STAGE_LOGIN)} />
        </div>
      </div>
    );
  }

  // -------- STAGE: password login --------
  if (stage === STAGE_LOGIN) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-md mx-auto mt-16 sm:mt-24 px-6">
          <LoginForm
            onSuccess={() => {
              adminAuth.me().then(setMe).catch(() => {});
              setStage(STAGE_DASHBOARD);
              loadDrugs();
            }}
            onBack={() => setStage(STAGE_GATE)}
          />
        </div>
      </div>
    );
  }

  // -------- STAGE: dashboard sub-pages --------
  if (openCard) {
    const back = () => setOpenCard(null);
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-6xl mx-auto px-6 py-10">
          {openCard === "drugs" && (
            <DrugManagement onBack={back} me={me}
              drugListSlot={
                <DrugListSlot drugs={drugs} loading={loading} stats={stats}
                  handleDelete={handleDelete}
                  canEdit={!!(me?.is_super_admin || (me?.permissions || []).includes("edit_drug") || (me?.permissions || []).includes("add_drug"))}
                  canDelete={!!me?.is_super_admin} />
              } />
          )}
          {openCard === "ai" && <AIModelsManagement onBack={back} me={me} />}
          {openCard === "account" && (
            <AccountSecurity onBack={back} me={me}
              onChangePw={() => setChangePwOpen(true)}
              onLogout={handleLogout} onLogoutAll={handleLogoutAll} />
          )}
        </div>
        {changePwOpen && (
          <ChangePasswordModal onClose={() => setChangePwOpen(false)}
            onDone={() => { setChangePwOpen(false); handleLogout(); }} />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8 border-b-2 border-slate-900 pb-6">
          <div className="text-sm font-bold tracking-tight text-slate-500 mb-2">
            دوائي / لوحة الإدارة
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
            لوحة تحكم الأدمن
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            إدارة الأدوية المقيّدة، مراقبة نماذج الذكاء الاصطناعي، وإعدادات الحساب
          </p>
        </div>

        <AdminDashboardCards
          handlers={{
            onOpenDrugs:   () => setOpenCard("drugs"),
            onOpenAI:      () => setOpenCard("ai"),
            onOpenAccount: () => setOpenCard("account"),
          }}
        />
      </div>

      {changePwOpen && (
        <ChangePasswordModal
          onClose={() => setChangePwOpen(false)}
          onDone={() => {
            setChangePwOpen(false);
            handleLogout();
          }}
        />
      )}
    </div>
  );
}

// Extracted so DrugManagement can render the same list — Admin.jsx owns the
// state (drugs, loading, stats, handleDelete) and passes it in as a prop.
export function DrugListSlot({ drugs, loading, stats, handleDelete, canDelete, canEdit }) {
  const fmt = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleString("ar-SA", { timeZone: "Asia/Riyadh", hour12: false }); }
    catch { return String(iso); }
  };
  return (
    <div data-testid="drug-list-slot">
      <div id="drug-list-section" className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <PillBottle className="w-5 h-5 text-slate-600" />
          <span>القائمة الحالية</span>
        </h2>
        <span data-testid="admin-total-drugs" className="text-sm text-slate-500 font-bold"
          title={`سجلات: ${stats.documents} — عربية فريدة: ${stats.unique_ar} — إنجليزية فريدة: ${stats.unique_en}`}>
          إجمالي الأدوية: {stats.total}
        </span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : drugs.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
          <p className="text-slate-500 mb-4">لا توجد أدوية بعد.</p>
          {canEdit && (
            <Link to="/admin/add" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-5 py-3 rounded-xl">
              <Plus className="w-4 h-4" /><span>إضافة دواء</span>
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {drugs.map((d) => (
            <div key={d.id} data-testid={`admin-drug-row-${d.id}`}
              className="bg-white border-2 border-slate-200 rounded-2xl p-5 flex items-start gap-4">
              <div className="w-11 h-11 shrink-0 rounded-xl bg-red-50 text-red-600 border-2 border-red-200 flex items-center justify-center font-bold">
                <span className="text-xs">مقيد</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-lg font-bold text-slate-900">{d.name}</span>
                  {d.name_en && <span className="text-sm text-slate-500" dir="ltr">({d.name_en})</span>}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{d.reason}</p>
                <div className="text-xs text-slate-400 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  <span data-testid={`drug-created-by-${d.id}`}>
                    أضيف بواسطة: <b className="text-slate-600">{d.created_by_username || "غير متاح"}</b>
                    {d.created_at && <> · <span dir="ltr">{fmt(d.created_at)}</span></>}
                  </span>
                  {d.updated_at && (
                    <span data-testid={`drug-updated-by-${d.id}`}>
                      آخر تعديل: <b className="text-slate-600">{d.updated_by_username || "غير متاح"}</b>
                      · <span dir="ltr">{fmt(d.updated_at)}</span>
                    </span>
                  )}
                </div>
              </div>
              {canEdit && (
                <Link to={`/admin/edit/${d.id}`} data-testid={`edit-drug-${d.id}`}
                  className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border-2 border-slate-200 flex items-center justify-center transition-colors" title="تعديل">
                  <Pencil className="w-4 h-4" />
                </Link>
              )}
              {canDelete && (
                <button data-testid={`delete-drug-${d.id}`} onClick={() => handleDelete(d)}
                  className="w-10 h-10 shrink-0 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 border-2 border-red-200 flex items-center justify-center transition-colors" title="حذف">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =================================================================================
// Sub-components
// =================================================================================

function GateForm({ onPass }) {
  const [birthdate, setBirthdate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lock, setLock] = useState(null); // { message, retry_after }
  const [remaining, setRemaining] = useState(0);
  const [inlineError, setInlineError] = useState("");

  // Live countdown while locked.
  useEffect(() => {
    if (!lock || !lock.retry_after) return;
    setRemaining(lock.retry_after);
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          setLock(null);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // setLock/setRemaining are stable React setters and don't belong in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lock]);

  const onChange = (e) => {
    setInlineError("");
    setBirthdate(formatBirthdate(e.target.value));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (lock) return;
    if (!BIRTHDATE_RE.test(birthdate)) {
      setInlineError("يرجى إدخال التاريخ كاملًا بصيغة YYYY/MM/DD");
      return;
    }
    setSubmitting(true);
    try {
      await adminAuth.gate(birthdate);
      toast.success("تم التحقق من الرمز");
      onPass();
    } catch (err) {
      const status = err?.response?.status;
      const lockDetail = getLockDetail(err);
      if (status === 429 && lockDetail) {
        setLock(lockDetail);
        toast.error(lockDetail.message);
      } else if (status === 429) {
        toast.error("محاولات كثيرة. حاول لاحقًا.");
      } else if (status === 401) {
        setInlineError("الرمز غير صحيح");
        toast.error("الرمز غير صحيح");
      } else {
        toast.error(formatErr(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const locked = Boolean(lock);

  return (
    <form
      data-testid="admin-gate-form"
      onSubmit={submit}
      className="fade-up p-8 bg-white border-2 border-slate-200 shadow-sm rounded-2xl text-right"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center">
          <Calendar className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">لوحة الإدارة</h1>
          <p className="text-sm text-slate-500 mt-0.5">أدخل رمز التحقق للمتابعة</p>
        </div>
      </div>

      <label className="block text-sm font-bold tracking-tight text-slate-500 mb-2">
        رمز التحقق (تاريخ الميلاد)
      </label>
      <input
        data-testid="admin-gate-input"
        type="text"
        value={birthdate}
        onChange={onChange}
        placeholder="YYYY/MM/DD"
        maxLength={10}
        disabled={locked}
        className="w-full bg-slate-50 border-2 border-slate-200 focus:outline-none focus:border-slate-900 rounded-xl px-4 py-4 text-slate-900 font-bold text-lg text-left tracking-widest disabled:opacity-60"
        autoFocus
        inputMode="numeric"
        autoComplete="off"
        dir="ltr"
      />
      <p className="text-xs text-slate-500 mt-2 mb-4" data-testid="admin-gate-helper">
        اكتب التاريخ بالأرقام فقط، وسيتم إضافة / تلقائيًا
      </p>

      {inlineError && !locked && (
        <div
          data-testid="admin-gate-inline-error"
          className="mb-4 text-sm font-bold text-red-700 bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3"
        >
          {inlineError}
        </div>
      )}

      {locked && (
        <div
          data-testid="admin-gate-lock-notice"
          className="mb-4 text-sm font-bold text-red-800 bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3 leading-relaxed"
        >
          <div>{lock.message}</div>
          {remaining > 0 && (
            <div className="text-xs font-bold text-red-700 mt-1">
              الوقت المتبقي: {humanRemaining(remaining)}
            </div>
          )}
        </div>
      )}

      <button
        data-testid="admin-gate-submit"
        type="submit"
        disabled={submitting || locked}
        className="w-full bg-slate-900 text-white font-bold py-4 rounded-xl hover:bg-slate-800 transition-colors text-lg cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {submitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>جارٍ التحقق</span>
          </>
        ) : (
          <>
            <ShieldCheck className="w-5 h-5" />
            <span>متابعة</span>
          </>
        )}
      </button>
    </form>
  );
}

function LoginForm({ onSuccess, onBack }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lock, setLock] = useState(null);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!lock || !lock.retry_after) return;
    setRemaining(lock.retry_after);
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          setLock(null);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // setLock/setRemaining are stable React setters and don't belong in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lock]);

  const submit = async (e) => {
    e.preventDefault();
    if (lock) return;
    if (!password) {
      toast.error("يرجى إدخال كلمة المرور");
      return;
    }
    setSubmitting(true);
    try {
      await adminAuth.login(password);
      toast.success("تم تسجيل الدخول");
      onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      const lockDetail = getLockDetail(err);
      if (status === 429 && lockDetail) {
        setLock(lockDetail);
        toast.error(lockDetail.message);
      } else if (status === 429) {
        toast.error("محاولات كثيرة. حاول لاحقًا.");
      } else if (status === 401 && (err?.response?.data?.detail || "").toString().includes("Gate")) {
        toast.error("انتهت الجلسة، أدخل الرمز مجددًا");
        onBack();
      } else if (status === 401) toast.error("كلمة المرور غير صحيحة");
      else toast.error(formatErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  const locked = Boolean(lock);

  return (
    <form
      data-testid="admin-login-form"
      onSubmit={submit}
      className="fade-up p-8 bg-white border-2 border-slate-200 shadow-sm rounded-2xl text-right"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center">
          <Lock className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">كلمة المرور</h1>
          <p className="text-sm text-slate-500 mt-0.5">الخطوة الثانية من التحقق</p>
        </div>
      </div>

      <label className="block text-sm font-bold tracking-tight text-slate-500 mb-2">
        كلمة مرور المدير
      </label>
      <input
        data-testid="admin-password-input"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
        disabled={locked}
        className="w-full bg-slate-50 border-2 border-slate-200 focus:outline-none focus:border-slate-900 rounded-xl px-4 py-4 text-slate-900 font-bold mb-6 text-lg text-right disabled:opacity-60"
        autoFocus
        autoComplete="current-password"
      />

      {locked && (
        <div
          data-testid="admin-login-lock-notice"
          className="mb-4 text-sm font-bold text-red-800 bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3 leading-relaxed"
        >
          <div>{lock.message}</div>
          {remaining > 0 && (
            <div className="text-xs font-bold text-red-700 mt-1">
              الوقت المتبقي: {humanRemaining(remaining)}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          data-testid="admin-login-back"
          type="button"
          onClick={onBack}
          className="px-5 py-4 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold rounded-xl transition-colors cursor-pointer"
        >
          رجوع
        </button>
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={submitting || locked}
          className="flex-1 bg-slate-900 text-white font-bold py-4 rounded-xl hover:bg-slate-800 transition-colors text-lg cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>جارٍ التحقق</span>
            </>
          ) : (
            <>
              <ShieldCheck className="w-5 h-5" />
              <span>دخول</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}

function ChangePasswordModal({ onClose, onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) {
      toast.error("جميع الحقول مطلوبة");
      return;
    }
    if (next !== confirm) {
      toast.error("كلمة المرور الجديدة والتأكيد غير متطابقين");
      return;
    }
    if (next.length < 8) {
      toast.error("كلمة المرور الجديدة يجب ألا تقل عن 8 أحرف");
      return;
    }
    setSaving(true);
    try {
      await adminAuth.changePassword(current, next, confirm);
      toast.success("تم تغيير كلمة المرور — سيتم تسجيل خروجك");
      onDone();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) toast.error("كلمة المرور الحالية غير صحيحة");
      else if (status === 429) toast.error("محاولات كثيرة. حاول لاحقًا.");
      else toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="change-pw-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="relative bg-white w-full max-w-md rounded-2xl border-2 border-slate-200 shadow-2xl p-6 text-right"
      >
        <button
          type="button"
          data-testid="change-pw-close"
          onClick={onClose}
          className="absolute top-4 left-4 w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">تغيير كلمة المرور</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              سيتم تسجيل خروجك بعد الحفظ.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <PwField
            label="كلمة المرور الحالية"
            testid="cpw-current"
            value={current}
            onChange={setCurrent}
          />
          <PwField
            label="كلمة المرور الجديدة (٨ أحرف على الأقل)"
            testid="cpw-new"
            value={next}
            onChange={setNext}
          />
          <PwField
            label="تأكيد كلمة المرور الجديدة"
            testid="cpw-confirm"
            value={confirm}
            onChange={setConfirm}
          />
        </div>

        <button
          data-testid="cpw-submit"
          type="submit"
          disabled={saving}
          className="mt-6 w-full bg-slate-900 text-white font-bold py-3.5 rounded-xl hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>جارٍ الحفظ</span>
            </>
          ) : (
            <>
              <KeyRound className="w-5 h-5" />
              <span>حفظ التغييرات</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}

const PwField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-sm font-bold tracking-tight text-slate-500 mb-2">{label}</label>
    <input
      data-testid={testid}
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-50 border-2 border-slate-200 focus:outline-none focus:border-slate-900 rounded-xl px-4 py-3 text-slate-900 font-bold text-right"
    />
  </div>
);
